import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText, Sparkles, RotateCcw, Home } from 'lucide-react';
import type {
  ContentFormatCode,
  SessionsHistoryResponse,
  SessionHistoryItem,
} from '@masha/shared';
import { api } from '../lib/api';
import { Badge, Button, Card, Spinner, Toast } from '../components/ui';
import { haptic } from '../lib/tma';

const PAGE_SIZE = 20;

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (isSameLocalDay(d, now)) return `Сегодня, ${time}`;
  if (isSameLocalDay(d, yesterday)) return `Вчера, ${time}`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) + `, ${time}`;
}

const FORMAT_LABEL: Record<ContentFormatCode, string> = {
  reels: 'Reels',
  carousel: 'Карусель',
  warming: 'Прогрев',
  lead_magnet: 'Лид-магнит',
};

export function HistoryPage() {
  const [items, setItems] = useState<SessionHistoryItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // First page via useQuery; refetchOnMount: 'always' гарантирует свежий
  // список после возврата с /sessions/:id (например, после rewrite — топовая
  // карточка должна показать обновлённое превью).
  const firstPageQuery = useQuery<SessionsHistoryResponse>({
    queryKey: ['sessions', 'history', 'page-0'],
    queryFn: () => api.listHistorySessions({ limit: PAGE_SIZE, offset: 0 }),
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  // Sync first-page result в local state. react-query v5 deprecated
  // onSuccess на useQuery, поэтому используем effect-pattern.
  // Refetch (focus, mount) сбрасывает юзера на первую страницу — это match'ит
  // намерение `refetchOnMount: 'always'` (видеть свежий top после rewrite).
  useEffect(() => {
    if (firstPageQuery.data) {
      setItems(firstPageQuery.data.items);
      setHasMore(firstPageQuery.data.has_more);
      setOffset(0);
    }
  }, [firstPageQuery.data]);

  async function handleLoadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const nextOffset = offset + PAGE_SIZE;
      const resp = await api.listHistorySessions({
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setItems((prev) => [...prev, ...resp.items]);
      setHasMore(resp.has_more);
      setOffset(nextOffset);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось загрузить';
      setLoadMoreError(msg);
    } finally {
      setLoadingMore(false);
    }
  }

  if (firstPageQuery.isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        <p className="text-sm text-tg-hint">Загружаем историю</p>
      </div>
    );
  }

  if (firstPageQuery.isError) {
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
        <h1 className="text-lg font-semibold mb-3">Не удалось загрузить</h1>
        <Button
          onClick={() => firstPageQuery.refetch()}
          variant="primary"
          size="lg"
          leftIcon={<RotateCcw size={18} />}
          className="w-full max-w-xs mb-2"
        >
          Повторить
        </Button>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
        >
          <Home size={14} />
          На главную
        </Link>
      </div>
    );
  }

  const isEmpty = items.length === 0;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <header className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
        <h1 className="text-lg font-semibold">История</h1>
        <span className="w-12" />
      </header>

      {isEmpty ? (
        <EmptyState />
      ) : (
        <>
          <ul className="space-y-3">
            {items.map((s) => (
              <HistoryCard key={s.id} item={s} />
            ))}
          </ul>

          {hasMore && (
            <Button
              onClick={handleLoadMore}
              disabled={loadingMore}
              loading={loadingMore}
              variant="secondary"
              size="md"
              fullWidth
              className="mt-4"
              hapticKind="light"
            >
              {loadingMore ? 'Загружаем' : 'Показать ещё'}
            </Button>
          )}

          {!hasMore && items.length > PAGE_SIZE && (
            <p className="text-xs text-tg-hint/70 text-center mt-4">Это всё, что есть.</p>
          )}

          {loadMoreError && (
            <Toast tone="destructive" className="mt-2 text-center">
              {loadMoreError}
            </Toast>
          )}
        </>
      )}
    </div>
  );
}

function HistoryCard({ item }: { item: SessionHistoryItem }) {
  const navigate = useNavigate();
  const dateStr = item.last_generation
    ? formatRelativeDate(item.last_generation.created_at)
    : formatRelativeDate(item.updated_at);
  const preview = item.last_generation?.content_preview ?? '';

  return (
    <li>
      <Card
        variant="surface"
        padding="sm"
        interactive
        role="button"
        tabIndex={0}
        onClick={() => {
          haptic('light');
          navigate(`/sessions/${item.id}?readonly=1`);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            haptic('light');
            navigate(`/sessions/${item.id}?readonly=1`);
          }
        }}
        className="text-left"
      >
        <div className="flex gap-3">
          {item.thumbnail_url && (
            <img
              src={item.thumbnail_url}
              alt=""
              className="w-14 shrink-0 aspect-[4/5] rounded-lg object-cover bg-tg-bg"
              loading="lazy"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs text-tg-hint">{dateStr}</p>
              <Badge tone={item.format_code === 'carousel' ? 'brand' : 'neutral'}>
                {FORMAT_LABEL[item.format_code]}
              </Badge>
            </div>
            <p className="text-sm font-medium mb-1 line-clamp-2 leading-snug">{item.idea_title}</p>
            {preview && (
              <p className="text-xs text-tg-hint line-clamp-2 leading-snug">{preview}</p>
            )}
          </div>
        </div>
      </Card>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-muted text-brand flex items-center justify-center">
        <FileText size={28} />
      </div>
      <h2 className="text-base font-semibold mb-2">Здесь будут ваши сценарии и карусели</h2>
      <p className="text-sm text-tg-hint mb-6 px-4">Создайте первый: выберите формат на главной.</p>
      <Link
        to="/"
        onClick={() => haptic('medium')}
        className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-brand text-brand-on text-base font-medium transition-[transform,opacity] duration-150 ease-smooth active:scale-[0.97]"
      >
        <Sparkles size={18} />
        Выбрать формат
      </Link>
    </div>
  );
}
